import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, inputClass, selectClass, channelOptions, relative, duration, Avatar, plain, asList } from './dashboardShared';

// The dashboard's Giveaway tab: what the giveaway cog's ?giveaway panel does, on the website. Entry is on cheeks.uk.

const PINK = '#ff73fa';
const GOLD = '#fbe510';
const QUICK = ['1h', '12h', '1d', '3d', '7d'];
const UNITS = { w: 604800, week: 604800, weeks: 604800, d: 86400, day: 86400, days: 86400, h: 3600, hr: 3600, hrs: 3600,
  hour: 3600, hours: 3600, m: 60, min: 60, mins: 60, minute: 60, minutes: 60, s: 1, sec: 1, secs: 1, second: 1, seconds: 1 };

// "3d", "1w2d", "12" (hours), "1 day 6 hours" → seconds, or null (the bot checks it again)
const parseDuration = (text) => {
  const t = (text || '').trim().toLowerCase();
  if (/^\d+(\.\d+)?$/.test(t)) return parseFloat(t) * 3600;
  const parts = [...t.matchAll(/(\d+(?:\.\d+)?)\s*([a-z]+)/g)];
  if (!parts.length || t.replace(/(\d+(?:\.\d+)?)\s*([a-z]+)/g, '').replace(/[\s,]|and/g, '')) return null;
  let total = 0;
  for (const [, n, unit] of parts) {
    if (!UNITS[unit]) return null;
    total += parseFloat(n) * UNITS[unit];
  }
  return total;
};

const dateTime = (ms) => new Date(ms).toLocaleString([], { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

// The giveaway message as Discord shows it. A running giveaway gives endsAt; the start form gives runsFor (seconds),
// shown as typed, so the preview doesn't flicker between "1d 0h" and "23h 59m" as the seconds tick by
const PostPreview = ({ prize, endsAt, runsFor, entries, image, ended, winners = [], cancelled }) => (
  <div className="rounded-xl bg-[#313338] p-3 text-[#dbdee1] text-sm" style={{ fontFamily: 'gg sans, "Noto Sans", Helvetica, Arial, sans-serif' }}>
    <div className="rounded bg-[#2b2d31] border-l-4 px-4 py-3 max-w-[520px]" style={{ borderLeftColor: cancelled ? '#4e5058' : ended ? GOLD : PINK }}>
      <p className="text-white text-xs font-semibold">🎉 Giveaway</p>
      <p className="text-white text-xl font-bold mt-1 break-words">{prize || 'Your prize'}</p>
      {cancelled ? <p>This giveaway was cancelled.</p> : ended ? (
        <>
          <p>Ended {endsAt ? relative(endsAt) : ''}</p>
          <p className="mt-2">🏆 <b>Winner{winners.length !== 1 ? 's' : ''}:</b> {winners.length ? winners.map(w => `@${w.name}`).join(', ') : 'No eligible entries'}</p>
        </>
      ) : (
        runsFor
          ? <p>Ends in {duration(runsFor * 1000)} ({dateTime(Math.floor((Date.now() + runsFor * 1000) / 60000) * 60000)})</p>
          : <p>Ends {endsAt ? relative(endsAt) : '…'} {endsAt ? `(${dateTime(endsAt)})` : ''}</p>
      )}
      {image && /^https?:\/\/\S+$/.test(image) && <img src={image} alt="" className="mt-3 rounded max-h-56 max-w-full object-contain" />}
      <p className="text-xs mt-2">CHΞΞKSCORD{cancelled ? '' : ` · Entries: ${(entries || 0).toLocaleString()}`}</p>
    </div>
    {!ended && !cancelled && (
      <span className="inline-flex items-center gap-1.5 mt-2 px-4 py-1.5 rounded bg-[#4e5058] text-white text-sm font-medium">🎉 Enter on cheeks.uk <span className="text-xs">↗</span></span>
    )}
  </div>
);

export default function GiveawayTab({ ask, lists, setNote, failed }) {
  const [state, setState] = useState(null); // { giveaway, log }
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');
  const [form, setForm] = useState({ prize: '', duration: '1d', winners: '1', image: '', channel: '' });
  const [picture, setPicture] = useState('');
  const [search, setSearch] = useState('');
  const [showLog, setShowLog] = useState(false);

  // The bot's answer, with every list filled in (Firebase drops empty ones)
  const tidy = (data) => ({
    log: asList(data?.log),
    giveaway: data?.giveaway ? {
      ...data.giveaway,
      winners: asList(data.giveaway.winners), entrants: asList(data.giveaway.entrants),
      entries: data.giveaway.entries || 0, winnersCount: data.giveaway.winnersCount || 0,
    } : null,
  });

  const load = useCallback(async () => {
    const answer = await ask('giveaway.state');
    if (answer.ok) setState(tidy(answer.data)); else failed(answer);
  }, [ask]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  // While one's running, keep the entry count fresh
  const running = state?.giveaway?.running;
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [running, load]);
  useEffect(() => { setPicture(state?.giveaway?.image || ''); }, [state?.giveaway?.image]);

  const act = async (verb, args = {}, needsConfirm = false) => {
    if (needsConfirm && confirm !== verb) { setConfirm(verb); return; }
    setConfirm(''); setBusy(verb); setNote(null);
    const answer = await ask(`giveaway.${verb}`, args);
    setBusy('');
    if (failed(answer)) return;
    setState(tidy(answer.data));
    if (answer.data?.message) setNote({ text: answer.data.message, ok: true });
  };

  const seconds = parseDuration(form.duration);
  const formProblem = !form.prize.trim() ? 'Type the prize.'
    : !seconds || seconds < 60 ? 'How long: e.g. 30m, 12h, 3d or 1w2d (at least a minute).'
      : !/^\d+$/.test(form.winners) || +form.winners < 1 || +form.winners > 50 ? 'Winners: a number from 1 to 50.'
        : form.image && !/^https?:\/\/\S+$/.test(form.image.trim()) ? 'The picture needs to be a full link starting https://.'
          : !form.channel ? 'Pick the channel to post it in.' : null;

  if (!state) return <Card><p className="text-zinc-500 text-xs">Loading…</p></Card>;
  const gw = state.giveaway;
  const entrants = asList(gw?.entrants).filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      {gw && (
        <Card title={gw.running ? '🎉 Running now' : gw.cancelled ? 'Last giveaway (cancelled)' : '🏆 Last giveaway (ended)'}
          right={gw.url && <a href={gw.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#00a8fc] hover:underline">See the post ↗</a>}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3 min-w-0">
              <div className="grid grid-cols-3 gap-2">
                {[['Entries', gw.entries.toLocaleString()],
                  [gw.running ? 'Ends' : 'Ended', relative(gw.endsAt)],
                  ['Winners', gw.running ? gw.winnersCount : gw.winners.length]].map(([label, value]) => (
                  <div key={label} className="rounded-lg bg-zinc-950/60 border border-zinc-800 px-2 py-2 min-w-0">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider">{label}</p>
                    <p className="text-white text-sm sm:text-base font-bold truncate">{value}</p>
                  </div>
                ))}
              </div>
              <p className="text-zinc-500 text-[11px]">In #{gw.channel || 'a deleted channel'} · {new Date(gw.endsAt).toLocaleString()}</p>

              {gw.running && (
                <>
                  <div className="flex flex-wrap gap-2">
                    <Button tone={confirm === 'end' ? 'success' : 'plain'} onClick={() => act('end', {}, true)} disabled={!!busy}>
                      {busy === 'end' ? 'Ending…' : confirm === 'end' ? `Yes: draw ${gw.winnersCount} from ${gw.entries}` : '🏁 End now'}
                    </Button>
                    <Button tone={confirm === 'cancel' ? 'danger' : 'ghost'} onClick={() => act('cancel', {}, true)} disabled={!!busy}>
                      {busy === 'cancel' ? 'Cancelling…' : confirm === 'cancel' ? 'Yes, cancel it (no winner)' : '🗑️ Cancel'}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <input value={picture} onChange={e => setPicture(e.target.value)} placeholder="Picture link (optional)" className={inputClass} />
                    <Button small onClick={() => act('image', { image: picture })} disabled={!!busy || picture === (gw.image || '')}>
                      {busy === 'image' ? '…' : 'Save picture'}
                    </Button>
                  </div>
                </>
              )}

              {!gw.running && !gw.cancelled && (
                <>
                  <div>
                    <p className="text-zinc-400 text-[11px] mb-1.5">Won by</p>
                    {gw.winners.length === 0 ? <p className="text-zinc-500 text-xs">No eligible entries.</p> : (
                      <div className="flex flex-wrap gap-1.5">
                        {gw.winners.map(w => (
                          <span key={w.id} className="flex items-center gap-1.5 rounded-full bg-zinc-800 pl-0.5 pr-2.5 py-0.5 text-xs text-white">
                            <Avatar src={w.avatar} name={w.name} size={20} />{w.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => act('reroll', { count: 1 })} disabled={!!busy}>{busy === 'reroll' ? 'Drawing…' : '🎲 Reroll a winner'}</Button>
                    <Button tone={confirm === 'clear' ? 'danger' : 'ghost'} onClick={() => act('clear', {}, true)} disabled={!!busy}>
                      {busy === 'clear' ? '…' : confirm === 'clear' ? 'Yes, take it off cheeks.uk' : '🧹 Clear from site'}
                    </Button>
                  </div>
                  <p className="text-zinc-600 text-[10px]">Winners show on cheeks.uk for 24 hours, or until it's cleared.</p>
                </>
              )}
            </div>
            <PostPreview prize={gw.prize} endsAt={gw.endsAt} entries={gw.entries} image={gw.image}
              ended={!gw.running} cancelled={gw.cancelled} winners={gw.winners} />
          </div>
        </Card>
      )}

      {gw && gw.entrants.length > 0 && (
        <Card title={`Entries · ${gw.entries.toLocaleString()}`} right={
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Find someone" className={`${inputClass} !w-40`} />
        }>
          <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
            {entrants.map(p => (
              <span key={p.id} title={p.here ? p.name : 'Left the server: can’t win'}
                className={`flex items-center gap-1.5 rounded-full bg-zinc-800 pl-0.5 pr-2.5 py-0.5 text-[11px] ${p.here ? 'text-white' : 'text-zinc-500 line-through'}`}>
                <Avatar src={p.avatar} name={p.name} size={18} />{p.name}
              </span>
            ))}
            {entrants.length === 0 && <p className="text-zinc-500 text-xs">Nobody by that name.</p>}
          </div>
          {gw.entries > gw.entrants.length && <p className="text-zinc-600 text-[10px] mt-2">Showing the newest {gw.entrants.length}.</p>}
        </Card>
      )}

      {!gw?.running && (
        <Card title="Start a giveaway">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2.5 min-w-0">
              <label className="block">
                <span className="block text-zinc-400 text-[11px] mb-1">Prize</span>
                <input value={form.prize} maxLength={200} onChange={e => setForm(f => ({ ...f, prize: e.target.value }))} placeholder="e.g. Prismatic Evolutions ETB" className={inputClass} />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-zinc-400 text-[11px] mb-1">How long</span>
                  <input value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} placeholder="30m, 12h, 3d, 1w2d" className={inputClass} />
                </label>
                <label className="block">
                  <span className="block text-zinc-400 text-[11px] mb-1">Winners</span>
                  <input type="number" min={1} max={50} value={form.winners} onChange={e => setForm(f => ({ ...f, winners: e.target.value }))} className={inputClass} />
                </label>
              </div>
              <div className="flex flex-wrap gap-1">
                {QUICK.map(q => (
                  <button key={q} type="button" onClick={() => setForm(f => ({ ...f, duration: q }))}
                    className={`px-2 py-0.5 rounded-full text-[11px] border ${form.duration === q ? 'border-[#ff73fa] text-white' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>{q}</button>
                ))}
              </div>
              <label className="block">
                <span className="block text-zinc-400 text-[11px] mb-1">Picture (optional)</span>
                <input value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))} placeholder="https://…" className={inputClass} />
              </label>
              <label className="block">
                <span className="block text-zinc-400 text-[11px] mb-1">Post it in</span>
                <select value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))} className={`w-full ${selectClass}`}>
                  <option value="">Pick a channel</option>
                  {channelOptions(lists.channels)}
                </select>
              </label>
              <div className="flex items-center gap-3">
                <Button tone="primary" disabled={!!formProblem || !!busy}
                  onClick={async () => { await act('start', { ...form, image: form.image.trim() }); }}>
                  {busy === 'start' ? 'Starting…' : '🎉 Start giveaway'}
                </Button>
                {formProblem && <span className="text-zinc-500 text-[11px]">{formProblem}</span>}
              </div>
              <p className="text-zinc-600 text-[10px]">The timer starts when it's posted. Entry is on cheeks.uk, only for members who've verified their Whatnot account.</p>
            </div>
            <div className="min-w-0">
              <p className="text-zinc-400 text-[11px] mb-1.5">Preview</p>
              <PostPreview prize={form.prize} runsFor={seconds || null} entries={0} image={form.image.trim()} />
            </div>
          </div>
        </Card>
      )}

      {state.log?.length > 0 && (
        <Card title="Recent entries from the site" right={<Button small tone="ghost" onClick={() => setShowLog(s => !s)}>{showLog ? 'Hide' : 'Show'}</Button>}>
          {showLog ? (
            <ul className="space-y-0.5 max-h-60 overflow-y-auto">
              {[...state.log].reverse().map((line, i) => <li key={i} className="text-zinc-400 text-[11px] font-mono break-words">{plain(line)}</li>)}
            </ul>
          ) : <p className="text-zinc-600 text-[11px]">What happened to each entry, to see why one didn't go in.</p>}
        </Card>
      )}
    </div>
  );
}
